import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FaSpinner } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatTimestamp = (value) => (value ? new Date(value).toLocaleString() : '-');

// Group usage rows by workflow_run_id without dropping any per-step row.
// Standalone rows (no workflow_run_id) each become their own group.
const groupByWorkflow = (items) => {
  const groups = [];
  const byRun = new Map();
  items.forEach((item) => {
    const runId = item.workflow_run_id;
    if (!runId) {
      groups.push({ runId: null, rows: [item] });
      return;
    }
    if (!byRun.has(runId)) {
      const group = { runId, rows: [] };
      byRun.set(runId, group);
      groups.push(group);
    }
    byRun.get(runId).rows.push(item);
  });
  return groups;
};

const CreditUsage = () => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const [usage, setUsage] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [usageRes, activityRes] = await Promise.all([
        fetch(`${API_BASE}/api/credits/usage`, { headers }),
        fetch(`${API_BASE}/api/credits/activity`, { headers }),
      ]);
      if (!usageRes.ok || !activityRes.ok) throw new Error('Failed to load credit history.');
      setUsage((await usageRes.json()).items || []);
      setActivity((await activityRes.json()).items || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load credit history.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <FaSpinner className="animate-spin w-8 h-8 text-brand-red" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <div className="bg-red-500/20 border border-red-500/30 text-red-500 px-4 py-3 rounded-lg text-sm">{error}</div>
        <button onClick={load} className="px-4 py-2 bg-brand-red text-white rounded-lg hover:bg-brand-red-dark transition text-sm font-medium">Retry</button>
      </div>
    );
  }

  const groups = groupByWorkflow(usage);
  const headingClass = `text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`;
  const mutedClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const rowClass = 'flex items-center justify-between py-2 text-sm';

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      {/* Usage */}
      <section>
        <h2 className={headingClass}>Credit Usage</h2>
        {usage.length === 0 ? (
          <p className={`text-sm mt-3 ${mutedClass}`}>No usage yet.</p>
        ) : (
          <div className="mt-3 space-y-4">
            {groups.map((group, gi) => (
              <div key={group.runId || `single-${gi}`} className={`rounded-lg border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'} p-4`}>
                {group.runId && (
                  <div className={`text-xs font-medium mb-2 ${mutedClass}`}>Workflow run {group.runId}</div>
                )}
                {group.rows.map((row, ri) => (
                  <div key={row.execution_id || `${gi}-${ri}`} className={rowClass}>
                    <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
                      {row.operation_display_name_snapshot || row.operation_key}
                    </span>
                    <span className="flex items-center gap-4">
                      <span className="font-medium text-brand-red">{Math.abs(row.amount)} credits</span>
                      <span className={`text-xs ${mutedClass}`}>{formatTimestamp(row.created_at)}</span>
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Activity */}
      <section>
        <h2 className={headingClass}>Account Activity</h2>
        {activity.length === 0 ? (
          <p className={`text-sm mt-3 ${mutedClass}`}>No activity yet.</p>
        ) : (
          <div className={`mt-3 rounded-lg border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'} px-4 divide-y ${isDarkMode ? 'divide-gray-700/50' : 'divide-gray-100'}`}>
            {activity.map((row, i) => (
              <div key={`${row.entry_type}-${row.created_at}-${i}`} className={rowClass}>
                <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
                  {row.entry_type}
                  {row.operation_display_name_snapshot ? ` - ${row.operation_display_name_snapshot}` : ''}
                </span>
                <span className="flex items-center gap-4">
                  <span className={`font-medium ${row.amount >= 0 ? 'text-green-500' : 'text-brand-red'}`}>
                    {row.amount >= 0 ? `+${row.amount}` : row.amount} credits
                  </span>
                  <span className={`text-xs ${mutedClass}`}>{formatTimestamp(row.created_at)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default CreditUsage;
